import { EventState, SubstitutionReason } from '@bp/planung-graphql-types';
import {
  Button,
  Checkbox,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  LazyLoader,
  Row,
  RowSelectionState,
  Table,
  TableColumns,
  Tooltip,
} from '@bp/ui-components';
import dayjs, { Dayjs } from 'dayjs';
import { observer } from 'mobx-react-lite';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { SubstitutionDataGroupEntry } from '../../../pages/Substitutions/Plan/PlanSubstitutions/PlanSubstitutions';
import {
  SubstitutionDataEntry,
  SubstitutionDataMissingTeacherEntry,
  SubstitutionDataSubstitutionTeacherEntry,
  SubstitutionDataTeacherEntry,
} from '../../../pages/Substitutions/Plan/PlanSubstitutions/utils';
import { use_ResetEventMutation } from '../../../types/planung-graphql-client-defs';
import { getTextForSubstitutionReason } from '../../../utils/events';
import { useSubstitutionStore } from '../SubstitutionProvider';

type SubstitutionEventsTableProps = {
  filter: string;
  data: SubstitutionEventsTableType[];
  selected: RowSelectionState;
  onSubstitute: (uuid: string) => void;
  onCancelEvent: (uuid: string) => void;
  onMergeEvent: (eventUuid: string) => void;
  onWorkOrder: (uuid: string) => void;
  onDifferentTime: (uuid: string) => void;
  onRowSelect: (row: Row<SubstitutionEventsTableType>) => void;
  onOutputChange: (uuid: string, output: boolean) => Promise<void>;
};

export type EventConflict = {
  state: EventState;
  affectedEntityUuid: string;
  conflictedEventUuid: string;
};

export type SubstitutionEventsTableType = {
  output: boolean;
  uuid: string;
  start: Dayjs;
  end: Dayjs;
  involvedClasses: SubstitutionDataEntry[];
  groupClassNames: string;
  originalGroupClassNames: string;
  groupClassNamesLong: string;
  originalGroupClassNamesLong: string;
  classes: SubstitutionDataEntry[];
  groups: SubstitutionDataGroupEntry[];
  subject: SubstitutionDataEntry;
  rooms: SubstitutionDataEntry[];
  teachers: SubstitutionDataTeacherEntry[];
  missingTeachers: SubstitutionDataMissingTeacherEntry[];
  substitutionTeachers: SubstitutionDataSubstitutionTeacherEntry[];
  action: string;
  comment: string;
  conflicts: EventConflict[];
  isSchoolEvent: boolean;
  substitutionReason: SubstitutionReason | null;
  substitutionComment: string | null;
  isCancelled: boolean;
  aggregatedEventOriginalGroupClassNames: string | null;
  aggregatedEventOriginalGroupClassNamesLong: string | null;
  originalTeachers: SubstitutionDataEntry[];
  originalRooms: SubstitutionDataEntry[];
  onSchoolTrip: boolean;
  doNotSubstitute: boolean;
  timeGridEntryShortName: string | null;
};

export const SubstitutionEventsTable = observer(
  ({
    filter,
    data,
    selected,
    onSubstitute,
    onCancelEvent,
    onMergeEvent,
    onWorkOrder,
    onDifferentTime,
    onRowSelect,
    onOutputChange,
  }: SubstitutionEventsTableProps) => {
    const { t } = useTranslation();
    const substitutionStore = useSubstitutionStore();
    const eventContext = useMemorizedCacheTag('EVENT');

    const { sorting, saveSorting } = useColumnsSort('substitution-events');
    const { columnVisibility, saveColumnVisibility } = useHiddenColumns('substitution-events', {});

    const [checkboxLoading, setCheckboxLoading] = useState<string[]>([]);

    const [loading, setLoading] = useState<string[]>([]);

    const [, resetEvent] = use_ResetEventMutation();

    const selectedDay = substitutionStore.selectedDay;
    const selectedDayStartString = selectedDay.startOf('day').utc().toISOString();
    const selectedDayEndString = selectedDay.endOf('day').utc().toISOString();

    function getState(row: SubstitutionEventsTableType): { text: string; node: ReactNode } {
      let color = '#ACD6FB';
      let tooltip = t('substitutions.normalLesson');

      if (row.conflicts.some((c) => c.state === EventState.PersonNotAvailable && !c.affectedEntityUuid) && row.output) {
        color = '#CB423A';
        tooltip = t('substitutions.personNotAvailable');
      } else if (row.conflicts.some((c) => c.state === EventState.PersonMissing) && row.output) {
        color = '#FCD05F';
        tooltip = t('substitutions.personMissing');
      } else if (row.conflicts.some((c) => c.state === EventState.PersonConflict)) {
        color = 'var(--color-grey-lighter)';
        tooltip = t('substitutions.personConflict');
      } else if (row.isSchoolEvent) {
        color = '#51A8F5';
        tooltip = t('schoolEvents.title', { count: 1 });
      }

      if (row.onSchoolTrip && !row.isSchoolEvent) {
        color = '#747486';
        tooltip = t('substitutions.onSchoolEvent');
      }

      if (row.doNotSubstitute && !row.onSchoolTrip) {
        color = '#FF9800';
        tooltip = t('substitutions.doNotSubstitute');
      }

      if (
        row.substitutionReason === SubstitutionReason.Substituted ||
        row.substitutionReason === SubstitutionReason.Aggregated ||
        row.substitutionReason === SubstitutionReason.SelfWork
      ) {
        color = '#4CAF50';
        tooltip = getTextForSubstitutionReason(row.substitutionReason);
      }
      if (row.substitutionReason === SubstitutionReason.Cancelled) {
        color = '#9C27B0';
        tooltip = getTextForSubstitutionReason(row.substitutionReason);
      }
      return {
        text: tooltip,
        node: (
          <Tooltip content={tooltip}>
            <div className='color-dot' style={{ backgroundColor: color }}></div>
          </Tooltip>
        ),
      };
    }

    const columns: TableColumns<SubstitutionEventsTableType>[] = [
      {
        id: 'output',
        header: t('substitutions.print'),
        size: 40,
        accessorKey: 'output',
        cell: ({ row }) => {
          if (checkboxLoading.includes(row.original.uuid)) {
            return (
              <LazyLoader
                noPadding={true}
                embedded={true}
                iconPosition={'left'}
                transparent={true}
                size={'s'}
                forceHeight={`${50 - 8 - 8}px`}
              />
            );
          }
          return (
            <Checkbox
              disabled={row.original.isSchoolEvent}
              name={`print-${row.original.uuid}`}
              onChange={async (value) => {
                if (checkboxLoading.includes(row.original.uuid)) return;
                setCheckboxLoading([...checkboxLoading, row.original.uuid]);
                await onOutputChange(row.original.uuid, value.target.checked);
                setCheckboxLoading(checkboxLoading.filter((uuid) => uuid !== row.original.uuid));
              }}
              checked={row.original.output}
            />
          );
        },
      },
      {
        id: 'state',
        header: t('substitutions.state'),
        accessorKey: 'state',
        sortingFn: ({ original: a }, { original: b }) => {
          const stateA = getState(a).text;
          const stateB = getState(b).text;
          return stateA < stateB ? -1 : stateA > stateB ? 1 : 0;
        },
        cell: ({ row }) => {
          return getState(row.original).node;
        },
        size: 45,
      },
      {
        id: 'timeframe',
        header: t('common.time'),
        accessorKey: 'timeframe',
        sortingFn: ({ original: a }, { original: b }) => {
          return dayjs(a.start).diff(b.start, 'minute');
        },
        isMultiline: true,
        cell: ({ row }) => {
          const start = dayjs(row.original.start);
          const end = dayjs(row.original.end);

          const differentStart =
            start.isBefore(selectedDayStartString, 'days') && !start.isSame(selectedDayStartString, 'days');

          const differentEnd = end.isAfter(selectedDayEndString, 'days') && !end.isSame(selectedDayEndString, 'days');

          const isFullDay = start.isSameOrBefore(selectedDayStartString) && end.isSameOrAfter(selectedDayEndString);

          return isFullDay ? (
            t('substitutions.fullDay')
          ) : differentEnd ? (
            `${t('substitutions.from')} ${start.format('HH:mm')}`
          ) : differentStart ? (
            `${t('common.until')} ${end.format('HH:mm')}`
          ) : (
            <>
              {start.format('HH:mm')}-&#8203;
              {end.format('HH:mm')}
            </>
          );
        },
      },
      {
        header: `${t('classes.title', { count: 2 })}/${t('groups.title', { count: 2 })}`,
        accessorKey: 'originalGroupClassNames',
        id: 'originalGroupClassNames',
        cell: ({ row }) => {
          const schoolEventClassesShort = row.original.groupClassNames;
          const schoolEventClassesLong = row.original.groupClassNamesLong;
          return (
            <Tooltip
              content={
                row.original.isSchoolEvent
                  ? schoolEventClassesLong
                  : row.original.substitutionReason === SubstitutionReason.Aggregated
                    ? t('substitutions.mergedEvents', {
                        original: row.original.originalGroupClassNamesLong,
                        target: row.original.aggregatedEventOriginalGroupClassNamesLong,
                      })
                    : row.original.originalGroupClassNamesLong
              }
            >
              {row.original.isSchoolEvent ? (
                schoolEventClassesShort
              ) : row.original.substitutionReason === SubstitutionReason.Aggregated ? (
                <>
                  {row.original.originalGroupClassNames} {'->'} {row.original.aggregatedEventOriginalGroupClassNames}
                </>
              ) : (
                row.original.originalGroupClassNames
              )}
            </Tooltip>
          );
        },
        size: 200,
      },
      {
        id: 'subjects',
        header: t('subject.title', { count: 1 }),
        accessorKey: 'subjects',
        sortingFn: ({ original: a }, { original: b }) => {
          const subjectA = a.subject.name;
          const subjectB = b.subject.name;
          return subjectA < subjectB ? -1 : subjectA > subjectB ? 1 : 0;
        },
        cell: ({ row }) => {
          return <Tooltip content={row.original.subject.name}>{row.original.subject.shortName}</Tooltip>;
        },
      },
      {
        id: 'rooms',
        header: t('rooms.title', { count: 2 }),
        accessorKey: 'rooms',
        cell: ({ row }) => {
          return (
            <Tooltip content={row.original.rooms.map((r) => r.name).join(', ')}>
              {row.original.rooms.map((r) => r.shortName).join(', ')}
            </Tooltip>
          );
        },
      },
      {
        id: 'missingTeachers',
        header: t('substitutions.missing'),
        accessorKey: 'missingTeachers',
        sortingFn: ({ original: a }, { original: b }) => {
          const teachersA = a.missingTeachers.map((t) => t.shortName).join(', ');
          const teachersB = b.missingTeachers.map((t) => t.shortName).join(', ');
          return teachersA < teachersB ? -1 : teachersA > teachersB ? 1 : 0;
        },
        cell: ({ row }) => {
          return (
            <Tooltip content={row.original.missingTeachers.map((t) => t.name).join(', ')}>
              {row.original.missingTeachers.map((t) => t.shortName).join(', ')}
            </Tooltip>
          );
        },
      },
      {
        id: 'teachers',
        header: t('persons.title', { count: 2 }),
        accessorKey: 'teachers',
        sortingFn: ({ original: a }, { original: b }) => {
          const teachersA = a.teachers.map((t) => t.shortName).join(', ');
          const teachersB = b.teachers.map((t) => t.shortName).join(', ');
          return teachersA < teachersB ? -1 : teachersA > teachersB ? 1 : 0;
        },
        cell: ({ row }) => {
          return (
            <Tooltip content={row.original.teachers.map((t) => t.name).join(', ')}>
              {row.original.teachers.map((t) => t.shortName).join(', ')}
            </Tooltip>
          );
        },
      },
      {
        id: 'action',
        header: t('substitutions.action'),
        accessorKey: 'action',
        size: 150, // Zusammengelegt muss reinpassen
        cell: ({ row }) => {
          return <Tooltip content={row.original.action}>{row.original.action}</Tooltip>;
        },
      },
      {
        id: 'comment',
        header: t('common.comment'),
        accessorKey: 'comment',
        cell: ({ row }) => {
          return <Tooltip content={row.original.comment}>{row.original.comment}</Tooltip>;
        },
      },
    ];

    const menu = (row: Row<SubstitutionEventsTableType>): DropdownMenuItem[] => {
      return [
        {
          disabled: row.original.isCancelled,
          label: t('substitutions.title', { count: 1 }),
          onClick: (e) => {
            const isSelected = selected[row.id];
            if (isSelected) {
              e.stopPropagation();
            }
            onSubstitute(row.original.uuid);
          },
        },
        {
          label: t('substitutions.cancelEvent'),
          onClick: (e) => {
            const isSelected = selected[row.id];
            if (isSelected) {
              e.stopPropagation();
            }
            onCancelEvent(row.original.uuid);
          },
          disabled: row.original.isCancelled,
        },
        {
          label: t('substitutions.mergeEvent'),
          onClick: (e) => {
            const isSelected = selected[row.id];
            if (isSelected) {
              e.stopPropagation();
            }
            onMergeEvent(row.original.uuid);
          },
          disabled: row.original.groups.length === 0 || row.original.isCancelled,
        },
        {
          label: t('substitutions.workOrder'),
          onClick: (e) => {
            const isSelected = selected[row.id];
            if (isSelected) {
              e.stopPropagation();
            }
            onWorkOrder(row.original.uuid);
          },
          disabled: row.original.isCancelled,
        },
        {
          label: t('substitutions.differentTime'),
          onClick: (e) => {
            const isSelected = selected[row.id];
            if (isSelected) {
              e.stopPropagation();
            }
            onDifferentTime(row.original.uuid);
          },
          disabled: true,
        },
        {
          type: 'ruler',
        },
        {
          label: t('substitutions.reset'),
          color: 'error',
          onClick: async (e) => {
            setLoading([...loading, row.original.uuid]);
            const isSelected = selected[row.id];
            if (isSelected) {
              e.stopPropagation();
            }
            await resetEvent(
              {
                eventUuid: row.original.uuid,
              },
              eventContext,
            );
            setLoading(loading.filter((uuid) => uuid !== row.original.uuid));
          },
        },
      ];
    };

    return (
      <Table<SubstitutionEventsTableType>
        columns={columns}
        data={data}
        showBorderRadius
        showShadow
        rowSelection={selected}
        onRowClick={(_e, row) => {
          onRowSelect(row);
        }}
        showRowHover
        selectOnClick
        showSelected
        showSort
        sorting={sorting}
        onSortingChange={saveSorting}
        showVisibility
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={saveColumnVisibility}
        globalFilter={filter}
        lastColWidth='36px'
        lastCol={(row) => {
          const currentLoading = loading.find((l) => l === row.original.uuid);
          return (
            <Dropdown
              disabled={row.original.isSchoolEvent}
              trigger={
                <Button
                  hierarchy='ghost'
                  icon={<DotsHorizontalIcon />}
                  isLoading={currentLoading ? currentLoading.length > 0 : false}
                  disabled={row.original.isSchoolEvent}
                />
              }
            >
              <DropdownMenu data={menu(row)} />
            </Dropdown>
          );
        }}
      />
    );
  },
);
